import { useState, useRef, useEffect } from 'react'
import { omitTransientProps } from '../../lib/omitTransientProps'
import { styled } from '@linaria/react'
import { motion, AnimatePresence } from 'framer-motion'
import { MinimalEmptyState } from '@ovasabi/ui-minimal'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { ChatIcon } from '../icons'

export interface ConversationMessage {
  id: string
  body: string
  senderId?: string
  senderLabel?: string
  createdAt?: string
}

export interface ConversationPanelProps {
  messages: readonly ConversationMessage[]
  currentActorId: string
  onSend: (body: string) => Promise<void> | void
  emptyTitle?: string
  emptyDescription?: string
  placeholder?: string
  sendLabel?: string
  disabled?: boolean
  readOnly?: boolean
}

const TEXTAREA_MAX_PX = 120

/* How far above the bottom still counts as "reading the latest". Clears the
   End marker's scroll margin plus a line or two. */
const FOLLOW_SLACK_PX = 160

/** The nearest ancestor that scrolls vertically — the shell's page scroll. */
const scrollParentOf = (el: HTMLElement | null): HTMLElement | null => {
  for (let node = el?.parentElement ?? null; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll') return node
  }
  return null
}

const Style = {
  Root: styled.section`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    gap: ${minimalVars.space.xs};
    min-width: 0;
    > [data-minimal='EmptyState'] {
      align-content: center;
      gap: ${minimalVars.space.xs};
      flex: 1;
      min-height: 0;
      overflow-y: auto;
    }
    @media (max-width: 767px) and (max-height: 600px) {
      > [data-minimal='EmptyState'] {
        padding: 8px;
        gap: 6px;
        > div:first-child { display: none; }
        h3 { font-size: 1rem; }
        p { margin: 0; font-size: 0.875rem; line-height: 1.4; }
      }
    }
  `,
  /* The log has its own inner scroll so messages scroll inside the container */
  Log: styled.div`
    display: flex;
    flex-direction: column;
    gap: ${minimalVars.space.xs};
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: ${minimalVars.space.sm};
    border: 1px solid ${minimalVars.color.borderSubtle};
    border-radius: ${minimalVars.radius.md};
    background: ${minimalVars.color.bgSurfaceAlt};
    overscroll-behavior: contain;
  `,
  /* Where "scroll to latest" lands; the margin keeps the newest message clear
     of the pinned composer. */
  End: styled.div`
    height: 0;
    scroll-margin-bottom: 24px;
  `,
// motion would pass `$` props to the DOM; styled-components used to strip them.
  Message: styled(omitTransientProps(motion.article))<{ $mine: boolean }>`
    align-self: ${({ $mine }) => ($mine ? 'flex-end' : 'flex-start')};
    display: grid;
    gap: 3px;
    width: fit-content;
    max-width: min(86%, 34rem);
  `,
  Bubble: styled.p<{ $mine: boolean }>`
    margin: 0;
    padding: 10px 14px;
    border-radius: ${({ $mine }) =>
      $mine
        ? '16px 16px 3px 16px'
        : '16px 16px 16px 3px'};
    background: ${({ $mine }) => ($mine ? minimalVars.color.brand : minimalVars.color.bgSurface)};
    color: ${({ $mine }) => ($mine ? minimalVars.color.textInverse : minimalVars.color.textPrimary)};
    font-size: ${minimalVars.typography.bodySize};
    line-height: 1.45;
    overflow-wrap: anywhere;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    border: 1px solid
      ${({ $mine }) => ($mine ? 'transparent' : minimalVars.color.borderSubtle)};
  `,
  Meta: styled.span<{ $mine: boolean }>`
    justify-self: ${({ $mine }) => ($mine ? 'end' : 'start')};
    color: ${minimalVars.color.textTertiary};
    font-size: ${minimalVars.typography.metaSize};
    padding: 0 ${minimalVars.space['2xs']};
    overflow-wrap: anywhere;
    min-width: 0;
  `,
  Composer: styled.form`
    flex-shrink: 0;
    display: flex;
    gap: ${minimalVars.space['2xs']};
    align-items: center;
    background: ${minimalVars.color.bgSurface};
    border: 1.5px solid ${minimalVars.color.borderStrong};
    border-radius: ${minimalVars.radius.pill};
    padding: 6px 10px 6px 16px;
    box-shadow: ${minimalVars.shadow.subtle};
    min-height: 46px;
    box-sizing: border-box;

    &:focus-within {
      border-color: ${minimalVars.color.brand};
      box-shadow: 0 0 0 2px ${minimalVars.color.brandSoft};
    }
  `,
  Textarea: styled.textarea`
    flex: 1;
    min-width: 0;
    height: 24px;
    min-height: 24px;
    max-height: ${TEXTAREA_MAX_PX}px;
    overflow-y: hidden;
    resize: none;
    padding: 2px 4px;
    border: none;
    background: transparent;
    color: ${minimalVars.color.textPrimary};
    font: inherit;
    font-size: max(1rem, ${minimalVars.typography.bodySize});
    line-height: 1.4;
    outline: none;

    &::placeholder {
      color: ${minimalVars.color.textTertiary};
    }
  `,
  SendButton: styled.button`
    cursor: pointer;
    border: none;
    background: ${minimalVars.color.brand};
    color: ${minimalVars.color.textInverse};
    font-weight: ${minimalVars.typography.weightBold};
    font-size: ${minimalVars.typography.captionSize};
    padding: 6px 16px;
    min-height: 44px;
    flex-shrink: 0;
    border-radius: ${minimalVars.radius.pill};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 120ms ease, transform 120ms ease;

    &:hover:not(:disabled) {
      opacity: 0.92;
      transform: scale(1.02);
    }
    &:active:not(:disabled) {
      transform: scale(0.96);
    }
    &:disabled {
      background: ${minimalVars.color.borderSubtle};
      color: ${minimalVars.color.textTertiary};
      cursor: not-allowed;
    }
  `,
  // Sits above the composer: below it, a sticky composer on a phone pushed
  // the error under the keyboard or the home indicator.
  Error: styled.p`
    flex-shrink: 0;
    margin: ${minimalVars.space.xs} ${minimalVars.space.sm} 0;
    padding: ${minimalVars.space.xs} ${minimalVars.space.sm};
    border-radius: ${minimalVars.radius.sm};
    background: ${minimalVars.color.dangerSoft};
    color: ${minimalVars.color.danger};
    font-size: 0.8125rem;
    line-height: 1.45;
    overflow-wrap: anywhere;
  `,
}

const formatMessageTime = (value?: string): string => {
  if (!value) return 'Just now'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Recently'
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Grow the textarea with its content, up to the cap (then it scrolls). */
const fitTextarea = (el: HTMLTextAreaElement | null) => {
  if (!el) return
  el.style.height = 'auto'
  if (el.scrollHeight > 0) el.style.height = `${Math.min(el.scrollHeight, TEXTAREA_MAX_PX)}px`
  else el.style.height = ''
  el.style.overflowY = el.scrollHeight > TEXTAREA_MAX_PX ? 'auto' : 'hidden'
}

export const ConversationPanel = ({
  messages,
  currentActorId,
  onSend,
  emptyTitle = 'Start the conversation',
  emptyDescription = 'Messages about this order will stay together here.',
  placeholder = 'Write a message…',
  sendLabel = 'Send',
  disabled = false,
  readOnly = false,
}: ConversationPanelProps) => {
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const rootRef = useRef<HTMLElement>(null)
  const endRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const followRef = useRef(true)
  const seenNewestRef = useRef('')
  const canSend = draft.trim().length > 0 && !sending && !disabled

  /* Whether the reader is at (or near) the newest message. The log lives in the
     page's scroll, so this watches the page's scroll container. */
  useEffect(() => {
    const scroller = scrollParentOf(rootRef.current)
    if (!scroller) return undefined
    const onScroll = () => {
      followRef.current =
        scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop <= FOLLOW_SLACK_PX
    }
    scroller.addEventListener('scroll', onScroll, { passive: true })
    return () => scroller.removeEventListener('scroll', onScroll)
  }, [])

  /* Bring the newest message into view when one arrives. Keyed on the newest
     id, not the array: the transcript is re-delivered by id on every read and
     projection delta, and following each re-delivery would yank the page back
     down while someone is reading further up.

     Always on opening the conversation and on your own send; for the other
     party's message, only if the reader was already at the bottom. Scrolling
     back through the order to check an address and being thrown to the end
     because the kitchen replied is what made the chat feel like it scrolled on
     its own. */
  const newest = messages.length > 0 ? messages[messages.length - 1] : undefined
  const newestId = newest?.id ?? ''
  const newestMine = Boolean(newest?.senderId && newest.senderId === currentActorId)
  useEffect(() => {
    if (!newestId) return
    const opening = !seenNewestRef.current
    seenNewestRef.current = newestId
    if (opening || newestMine || followRef.current) {
      endRef.current?.scrollIntoView?.({ block: 'end' })
      followRef.current = true
    }
  }, [newestId, newestMine])

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canSend) return
    const body = draft.trim()
    try {
      setSending(true)
      setError('')
      await onSend(body)
      setDraft('')
      if (textareaRef.current) {
        textareaRef.current.style.height = ''
        textareaRef.current.style.overflowY = ''
      }
    } catch {
      setError('We could not send that message. Check your connection and try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <Style.Root ref={rootRef} data-chow="ConversationPanel">
      {messages.length === 0 ? (
        <MinimalEmptyState icon={<ChatIcon />} title={emptyTitle} description={emptyDescription} />
      ) : (
        <Style.Log role="log" aria-live="polite" aria-label="Conversation">
          <AnimatePresence initial={false}>
            {messages.map((message) => {
              const mine = Boolean(message.senderId && message.senderId === currentActorId)
              return (
                <Style.Message
                  key={message.id}
                  $mine={mine}
                  /* Transform-only, and no exit: a message must be readable on
                     its first frame even if the animation clock is stopped (a
                     backgrounded app), and an evicted row must not linger. */
                  initial={{ scale: 0.96, y: 8 }}
                  animate={{ scale: 1, y: 0 }}
                  transition={{ type: 'spring', stiffness: 380, damping: 26 }}
                >
                  <Style.Bubble $mine={mine}>{message.body}</Style.Bubble>
                  <Style.Meta $mine={mine}>
                    {mine && !readOnly ? 'You' : message.senderLabel ?? 'Order team'} · {formatMessageTime(message.createdAt)}
                  </Style.Meta>
                </Style.Message>
              )
            })}
          </AnimatePresence>
          <Style.End ref={endRef} aria-hidden />
        </Style.Log>
      )}
      {error ? <Style.Error role="alert">{error}</Style.Error> : null}
      {!readOnly && <Style.Composer onSubmit={submit} aria-label="Reply">
        <Style.Textarea
          ref={textareaRef}
          rows={1}
          value={draft}
          maxLength={2000}
          disabled={disabled || sending}
          placeholder={placeholder}
          onChange={(event) => {
            setDraft(event.target.value)
            fitTextarea(event.target)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              event.currentTarget.form?.requestSubmit()
            }
          }}
          aria-label="Message"
        />
        <Style.SendButton type="submit" disabled={!canSend}>
          {sending ? '...' : sendLabel}
        </Style.SendButton>
      </Style.Composer>}
    </Style.Root>
  )
}
